// MessageSystem.js - With @mentions, File Attachments, Lucide Icon Emoji Picker & Role-Based Access

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  Send, Check, CheckCheck,
  Info, Bell, Search, X,
  UserPlus, Loader2,
  Smile, Paperclip, FileText, File,
  Image, Film, Music, Archive,
  AtSign, Download, FileImage,
  Laugh, ThumbsUp, Heart, Star, Flame,
  AlertTriangle, Lightbulb, Megaphone,ArrowDown,
  BellRing, Mail, Smartphone, Monitor,
  PartyPopper, DollarSign, Calendar, Clock,
  Lock, Key, Pin, Scissors, PaperclipIcon,
  Folder, BarChart3, ShoppingCart, Home, Car,
  Plane, Coffee, Pizza, Music2, Camera, ZoomIn,
  MessageCircle, PenTool, Angry, Frown, Annoyed,
  Rocket, Gift, Trophy, Sparkles, Gem, Cake, GlassWater,
  Crown, Shield, Wrench, User, ClipboardList,
  Globe, Users, Hash, CheckCircle,
  XCircle, Ban, ArrowUp, MoreHorizontal
} from 'lucide-react';
import '../styles/MessageSystem.css';
import ourzestLogo from '../assets/images/ourzest_logo.png';

const API_BASE_URL = 'https://ourzestapp.online/oz_backend';

// ==============================================
// EMOJI CATEGORIES WITH LUCIDE ICONS
// ==============================================
const EMOJI_CATEGORIES_DATA = {
  'Smileys': {
    icon: <Smile size={16} />,
    items: [
      { name: 'Grinning', icon: <Smile size={20} />, search: 'smile grin happy face laugh' },
      { name: 'Laughing', icon: <Laugh size={20} />, search: 'laugh tears joy funny lol haha' },
      { name: 'Thumbs Up', icon: <ThumbsUp size={20} />, search: 'thumbs up like ok approve yes' },
      { name: 'Heart', icon: <Heart size={20} />, search: 'heart love red like favorite' },
      { name: 'Star', icon: <Star size={20} />, search: 'star favorite rate bookmark' },
      { name: 'Party', icon: <PartyPopper size={20} />, search: 'party celebrate celebration confetti fun' },
      { name: 'Rocket', icon: <Rocket size={20} />, search: 'rocket launch startup space fast' },
      { name: 'Gift', icon: <Gift size={20} />, search: 'gift present surprise birthday' },
      { name: 'Trophy', icon: <Trophy size={20} />, search: 'trophy winner champion award prize' },
      { name: 'Sparkles', icon: <Sparkles size={20} />, search: 'sparkles star glow shining magic' },
      { name: 'Gem', icon: <Gem size={20} />, search: 'diamond gem precious jewel' },
      { name: 'Cake', icon: <Cake size={20} />, search: 'cake birthday celebrate dessert' },
      { name: 'Glass', icon: <GlassWater size={20} />, search: 'drink cheers toast champagne' },
      { name: 'Angry', icon: <Angry size={20} />, search: 'angry mad rage furious' },
      { name: 'Frown', icon: <Frown size={20} />, search: 'sad cry tears unhappy frown' },
      { name: 'Annoyed', icon: <Annoyed size={20} />, search: 'annoyed frustrated upset grumpy' },
      { name: 'Cool', icon: <Smile size={20} />, search: 'cool sunglasses awesome' },
      { name: 'Think', icon: <MoreHorizontal size={20} />, search: 'think thinking hmm wonder' },
      { name: 'Pray', icon: <Users size={20} />, search: 'pray please thanks gratitude bless' },
      { name: 'Strong', icon: <ThumbsUp size={20} />, search: 'strong muscle strength power flex' },
      { name: 'Clap', icon: <ThumbsUp size={20} />, search: 'clap applause bravo congratulations' },
      { name: 'Fire', icon: <Flame size={20} />, search: 'fire hot flame lit awesome' },
      { name: '100', icon: <CheckCircle size={20} />, search: '100 perfect hundred score complete' },
      { name: 'Target', icon: <Star size={20} />, search: 'target goal bullseye aim objective' },
    ]
  },
  'Objects': {
    icon: <Folder size={16} />,
    items: [
      { name: 'Light Bulb', icon: <Lightbulb size={20} />, search: 'idea light bulb bright creative' },
      { name: 'Bell', icon: <BellRing size={20} />, search: 'bell notification alert ring alarm' },
      { name: 'Mail', icon: <Mail size={20} />, search: 'email mail message inbox' },
      { name: 'Phone', icon: <Smartphone size={20} />, search: 'phone mobile smartphone cell' },
      { name: 'Monitor', icon: <Monitor size={20} />, search: 'computer laptop pc desktop monitor' },
      { name: 'Calendar', icon: <Calendar size={20} />, search: 'calendar date schedule event' },
      { name: 'Clock', icon: <Clock size={20} />, search: 'clock alarm time watch timer' },
      { name: 'Lock', icon: <Lock size={20} />, search: 'lock secure security private protect' },
      { name: 'Key', icon: <Key size={20} />, search: 'key password access unlock' },
      { name: 'Pin', icon: <Pin size={20} />, search: 'pin pushpin tack location' },
      { name: 'Scissors', icon: <Scissors size={20} />, search: 'scissors cut trim crop' },
      { name: 'Paperclip', icon: <PaperclipIcon size={20} />, search: 'paperclip attach clip file' },
      { name: 'Folder', icon: <Folder size={20} />, search: 'folder file directory document' },
      { name: 'Chart', icon: <BarChart3 size={20} />, search: 'chart graph statistics data analytics' },
      { name: 'Cart', icon: <ShoppingCart size={20} />, search: 'cart shopping trolley buy purchase' },
      { name: 'Home', icon: <Home size={20} />, search: 'home house building residence' },
      { name: 'Car', icon: <Car size={20} />, search: 'car vehicle auto drive transport' },
      { name: 'Plane', icon: <Plane size={20} />, search: 'airplane flight plane travel fly' },
      { name: 'Coffee', icon: <Coffee size={20} />, search: 'coffee drink tea cafe beverage' },
      { name: 'Pizza', icon: <Pizza size={20} />, search: 'pizza food meal eat hungry' },
      { name: 'Music', icon: <Music2 size={20} />, search: 'music note song audio sound melody' },
      { name: 'Camera', icon: <Camera size={20} />, search: 'camera photo picture image snapshot' },
      { name: 'Search', icon: <ZoomIn size={20} />, search: 'search magnify zoom find look' },
      { name: 'Chat', icon: <MessageCircle size={20} />, search: 'speech bubble chat message talk conversation' },
      { name: 'Pen', icon: <PenTool size={20} />, search: 'memo note write document edit pen' },
    ]
  },
  'Symbols': {
    icon: <Hash size={16} />,
    items: [
      { name: 'Check', icon: <CheckCircle size={20} />, search: 'check mark done complete tick correct yes' },
      { name: 'Cross', icon: <XCircle size={20} />, search: 'cross x wrong cancel delete no close' },
      { name: 'Warning', icon: <AlertTriangle size={20} />, search: 'warning caution alert danger careful' },
      { name: 'Ban', icon: <Ban size={20} />, search: 'ban block stop forbidden prohibited' },
      { name: 'Info', icon: <Info size={20} />, search: 'info information details about' },
      { name: 'Bell', icon: <Bell size={20} />, search: 'bell notification ring' },
      { name: 'Megaphone', icon: <Megaphone size={20} />, search: 'announcement loud speaker megaphone broadcast' },
      { name: 'Up', icon: <ArrowUp size={20} />, search: 'arrow up increase rise top' },
      { name: 'Down', icon: <ArrowDown size={20} />, search: 'arrow down decrease fall bottom' },
      { name: 'Globe', icon: <Globe size={20} />, search: 'globe world earth global internet' },
      { name: 'Users', icon: <Users size={20} />, search: 'users people group team community' },
      { name: 'Crown', icon: <Crown size={20} />, search: 'crown king queen royal leader admin' },
      { name: 'Shield', icon: <Shield size={20} />, search: 'shield protect security defense safe' },
      { name: 'Wrench', icon: <Wrench size={20} />, search: 'wrench tool fix repair settings' },
      { name: 'User', icon: <User size={20} />, search: 'user person profile account' },
      { name: 'Clipboard', icon: <ClipboardList size={20} />, search: 'clipboard list notes tasks todo' },
      { name: 'Dollar', icon: <DollarSign size={20} />, search: 'money dollar cash currency price cost' },
    ]
  }
};

// ==============================================
// BUILT-IN EMOJI PICKER COMPONENT (LUCIDE ICONS)
// ==============================================
const BuiltInEmojiPicker = ({ onEmojiSelect, darkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Smileys');

  // Get all items from all categories
  const allItems = Object.values(EMOJI_CATEGORIES_DATA).flatMap(cat => cat.items);
  
  // Filter by search
  const filteredItems = searchTerm
    ? allItems.filter(item => 
        item.search.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Get current category items
  const categoryItems = searchTerm 
    ? filteredItems 
    : (EMOJI_CATEGORIES_DATA[activeCategory]?.items || []);

  const handleSelect = (item) => {
    // Send the icon name as the emoji identifier
    onEmojiSelect(item);
  };

  return (
    <div className={`built-in-emoji-picker ${darkMode ? 'dark' : 'light'}`}>
      {/* Search Bar */}
      <div className="emoji-search-bar">
        <Search size={14} className="emoji-search-icon" />
        <input
          type="text"
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="emoji-search-input"
          autoFocus
        />
        {searchTerm && (
          <button className="emoji-search-clear" onClick={() => setSearchTerm('')}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Category Tabs */}
      {!searchTerm && (
        <div className="emoji-categories">
          {Object.entries(EMOJI_CATEGORIES_DATA).map(([category, data]) => (
            <button
              key={category}
              className={`emoji-category-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
              title={category}
            >
              {data.icon}
            </button>
          ))}
        </div>
      )}

      {/* Icon Grid */}
      <div className="emoji-grid">
        {categoryItems.length > 0 ? (
          categoryItems.map((item, index) => (
            <button
              key={`${activeCategory}-${index}`}
              className="emoji-item lucide-emoji-item"
              onClick={() => handleSelect(item)}
              title={item.name}
            >
              {item.icon}
            </button>
          ))
        ) : (
          <div className="no-emoji-found">
            <Search size={20} />
            <span>No icons found</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="emoji-footer">
        <span>{searchTerm ? `${filteredItems.length} results` : `${categoryItems.length} icons`}</span>
      </div>
    </div>
  );
};

// ==============================================
// MAIN MESSAGE SYSTEM COMPONENT
// ==============================================
const MessageSystem = () => {
  const { user } = useAuth();

  // Theme State
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('ourzest_darkMode') === 'true';
  });
  const [currentThemeColor, setCurrentThemeColor] = useState(() => {
    return localStorage.getItem('ourzest_theme') || '#00A884';
  });

  // State
  const [allMessages, setAllMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageTypes] = useState(getMessageTypes());
  const [filterType, setFilterType] = useState('all');
  
  // Quick message state
  const [quickMessage, setQuickMessage] = useState('');
  const [showQuickEmojiPicker, setShowQuickEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [insertedIcons, setInsertedIcons] = useState([]);
  
  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [cursorPosition, setCursorPosition] = useState(0);
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalMessages, setTotalMessages] = useState(0);
  
  const quickInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  // ==============================================
  // HELPER FUNCTIONS WITH LUCIDE ICONS
  // ==============================================
  function getMessageTypes() {
    return {
      system_update: { label: 'System Update', icon: <RefreshCw size={12} />, color: '#3B82F6' },
      information: { label: 'Information', icon: <Info size={12} />, color: '#10B981' },
      changes: { label: 'Changes', icon: <PenTool size={12} />, color: '#F59E0B' },
      updates: { label: 'Updates', icon: <Megaphone size={12} />, color: '#8B5CF6' },
      alert: { label: 'Alert', icon: <AlertTriangle size={12} />, color: '#EF4444' },
      info: { label: 'Info', icon: <ClipboardList size={12} />, color: '#6B7280' },
      server_down: { label: 'Server Down', icon: <Ban size={12} />, color: '#DC2626' },
      maintenance: { label: 'Maintenance', icon: <Wrench size={12} />, color: '#F97316' },
      announcement: { label: 'Announcement', icon: <Megaphone size={12} />, color: '#EC4899' }
    };
  }

  function getUserProfiles() {
    return {
      super_admin: { label: 'Super Admin', icon: <Crown size={14} />, color: '#8B5CF6' },
      admin_pos: { label: 'Admin POS', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_fabrics: { label: 'Admin Fabrics', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_mechanicsap: { label: 'Admin Mechanics AP', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_hardwareap: { label: 'Admin Hardware AP', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_autosap: { label: 'Admin Autos AP', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_pharmacy: { label: 'Admin Pharmacy', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_cosmetics: { label: 'Admin Cosmetics', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_solar: { label: 'Admin Solar', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_electronics: { label: 'Admin Electronics', icon: <Shield size={14} />, color: '#3B82F6' },
      admin_tech: { label: 'Technical Team', icon: <Wrench size={14} />, color: '#F59E0B' },
      user_pos: { label: 'User POS', icon: <User size={14} />, color: '#6B7280' },
      user_fabrics: { label: 'User Fabrics', icon: <User size={14} />, color: '#6B7280' },
      user_mechanicsap: { label: 'User Mechanics AP', icon: <User size={14} />, color: '#6B7280' },
      user_hardwareap: { label: 'User Hardware AP', icon: <User size={14} />, color: '#6B7280' },
      user_autosap: { label: 'User Autos AP', icon: <User size={14} />, color: '#6B7280' },
      user_pharmacy: { label: 'User Pharmacy', icon: <User size={14} />, color: '#6B7280' },
      user_cosmetics: { label: 'User Cosmetics', icon: <User size={14} />, color: '#6B7280' },
      user_solar: { label: 'User Solar', icon: <User size={14} />, color: '#6B7280' },
      user_electronics: { label: 'User Electronics', icon: <User size={14} />, color: '#6B7280' },
      general: { label: 'General', icon: <ClipboardList size={14} />, color: '#10B981' }
    };
  }

  // ==============================================
  // THEME SYNC
  // ==============================================
  const applyTheme = useCallback((color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = '#00A884';
    }
    document.documentElement.style.setProperty('--ozui-primary', color);
    document.documentElement.style.setProperty('--ozui-primary-dark', color);
    document.documentElement.style.setProperty('--ozui-primary-light', color);
    setCurrentThemeColor(color);
  }, []);

  useEffect(() => {
    const syncDarkMode = () => {
      const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
      setDarkMode(isDarkMode);
    };
    syncDarkMode();
    const handleStorage = (e) => {
      if (e.key === 'ourzest_darkMode') syncDarkMode();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem('ourzest_theme') || '#00A884';
    applyTheme(savedTheme);
    const handleStorage = (e) => {
      if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#00A884');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [applyTheme]);

  // ==============================================
  // EXTRACT USER INFO
  // ==============================================
  useEffect(() => {
    if (user) {
      const id = user.id || user.user_id || user.ID || null;
      const role = (user.role || user.user_role || '').toString().toLowerCase();
      setUserId(id);
      setIsSuperAdmin(role === 'super_admin');
    } else {
      setLoading(false);
    }
  }, [user]);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowQuickEmojiPicker(false);
      }
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(e.target)) {
        setShowMentionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ==============================================
  // SEARCH MESSAGES
  // ==============================================
  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      setFilteredMessages(allMessages);
      return;
    }
    const query = searchQuery.toLowerCase().trim();
    const filtered = allMessages.filter(msg => {
      if (!msg) return false;
      const title = (msg.message_title || '').toLowerCase();
      const content = (msg.message_content || '').toLowerCase();
      const senderName = (msg.sender_name || '').toLowerCase();
      return title.includes(query) || content.includes(query) || senderName.includes(query);
    });
    setFilteredMessages(filtered);
  }, [searchQuery, allMessages]);

  // ==============================================
  // LOAD USERS AND ROLES
  // ==============================================
  const loadUsers = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/root_users.php`, {
        params: { action: 'list', limit: 200, status: 'active' }
      });
      if (response.data && response.data.success) {
        const users = (response.data.data || []).filter(u => u && u.id && u.role);
        setAllUsers(users);
        const roles = [...new Set(users.map(u => u.role).filter(Boolean))];
        const userProfiles = getUserProfiles();
        const roleOptions = roles.map(role => ({
          value: role,
          label: (userProfiles[role] && userProfiles[role].label) || role,
          icon: userProfiles[role]?.icon || <User size={14} />
        }));
        setAllRoles(roleOptions);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // ==============================================
  // @MENTION HANDLING
  // ==============================================
  const handleQuickMessageChange = (e) => {
    if (!e || !e.target) return;
    const value = e.target.value || '';
    const cursorPos = e.target.selectionStart || 0;
    setQuickMessage(value);
    setCursorPosition(cursorPos);
    
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if (charBeforeAt === ' ' || atIndex === 0) {
        const searchText = textBeforeCursor.substring(atIndex + 1);
        if (!searchText.includes(' ')) {
          setMentionStartIndex(atIndex);
          setShowMentionDropdown(true);
          searchMentions(searchText);
          return;
        }
      }
    }
    setShowMentionDropdown(false);
  };

  const searchMentions = (query) => {
    const lowerQuery = (query || '').toLowerCase();
    let results = [];
    
    if ('all'.includes(lowerQuery) || !query) {
      results.push({
        type: 'all',
        label: '@all - All Users',
        icon: <Globe size={16} />,
        description: 'Send to all users'
      });
    }
    
    if (allRoles && allRoles.length > 0) {
      allRoles.forEach(role => {
        if (!role || !role.value) return;
        const roleValue = (role.value || '').toLowerCase();
        const roleLabel = (role.label || '').toLowerCase();
        if (roleValue.includes(lowerQuery) || roleLabel.includes(lowerQuery)) {
          results.push({
            type: 'role',
            value: role.value,
            label: `@${role.value} - ${role.label || role.value}`,
            icon: role.icon || <User size={16} />,
            description: `All ${role.label || role.value} users`
          });
        }
      });
    }
    
    if (query && query.length >= 1 && allUsers && allUsers.length > 0) {
      allUsers.forEach(u => {
        if (!u) return;
        const username = (u.username || '').toLowerCase();
        const fullname = (u.fullname || '').toLowerCase();
        if (username.includes(lowerQuery) || fullname.includes(lowerQuery)) {
          results.push({
            type: 'user',
            value: u.username || u.fullname || `user_${u.id}`,
            id: u.id,
            label: `@${u.username || u.fullname || `user_${u.id}`}`,
            icon: <User size={16} />,
            description: u.fullname || u.username || `User ${u.id}`
          });
        }
      });
    }
    
    setMentionResults(results.slice(0, 8));
  };

  const selectMention = (mention) => {
    if (!mention) return;
    const beforeMention = quickMessage.substring(0, mentionStartIndex);
    const afterCursor = quickMessage.substring(cursorPosition);
    let mentionText = '';
    if (mention.type === 'all') mentionText = '@all ';
    else if (mention.type === 'role') mentionText = `@${mention.value || ''} `;
    else if (mention.type === 'user') mentionText = `@${mention.value || ''} `;
    
    const newValue = beforeMention + mentionText + afterCursor;
    setQuickMessage(newValue);
    setShowMentionDropdown(false);
    
    setTimeout(() => {
      if (quickInputRef.current) {
        quickInputRef.current.focus();
        const newCursorPos = beforeMention.length + mentionText.length;
        quickInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 50);
  };

  const parseMessageWithMentions = (message) => {
    if (!message || typeof message !== 'string') return '';
    return message.replace(/@(\w+)/g, (match, mention) => {
      if (mention === 'all') return '<span class="mention-tag mention-all">@all</span>';
      else if (allRoles && allRoles.find(r => r && r.value === mention)) return `<span class="mention-tag mention-role">@${mention}</span>`;
      else return `<span class="mention-tag mention-user">@${mention}</span>`;
    });
  };

  // ==============================================
  // FILE HANDLING
  // ==============================================
  const handleFileSelect = (e) => {
    if (!e || !e.target || !e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setError('File too large. Maximum size is 20MB');
      return;
    }
    setSelectedFile(file);
    if (file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview({ type: 'image', url: e.target.result, name: file.name });
      reader.readAsDataURL(file);
    } else {
      setFilePreview({ type: 'file', name: file.name, size: file.size });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ==============================================
  // ICON/EMOJI HANDLING
  // ==============================================
  const handleEmojiSelect = (item) => {
    if (!item || !quickInputRef.current) return;
    const iconName = `:${item.name}:`;
    const start = quickInputRef.current.selectionStart || 0;
    const end = quickInputRef.current.selectionEnd || 0;
    const newValue = quickMessage.substring(0, start) + iconName + quickMessage.substring(end);
    setQuickMessage(newValue);
    setInsertedIcons(prev => [...prev, { name: item.name, icon: item.icon }]);
    
    setTimeout(() => {
      if (quickInputRef.current) {
        quickInputRef.current.focus();
        const newPos = start + iconName.length;
        quickInputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 50);
  };

  // ==============================================
  // FETCH MESSAGES
  // ==============================================
  const fetchMessages = useCallback(async (reset = false) => {
    if (!userId) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const currentPage = reset ? 1 : page;
      const params = {
        action: 'get_messages',
        user_id: userId,
        limit: 50,
        offset: (currentPage - 1) * 50
      };
      if (filterType && filterType !== 'all') params.message_type = filterType;
      
      const response = await axios.get(`${API_BASE_URL}/root_communication.php`, { params });
      if (response.data && response.data.success) {
        const newMessages = response.data.data || [];
        if (reset) {
          setAllMessages(newMessages);
          setFilteredMessages(newMessages);
          setPage(1);
        } else {
          setAllMessages(prev => [...prev, ...newMessages]);
          setFilteredMessages(prev => [...prev, ...newMessages]);
        }
        setTotalMessages(response.data.total || 0);
        setHasMore(newMessages.length === 50);
        setUnreadCount(response.data.unread_count || 0);
      } else {
        setError((response.data && response.data.error) || 'Failed to fetch messages');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError((error.response && error.response.data && error.response.data.error) || error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [userId, page, filterType]);

  useEffect(() => {
    const init = async () => {
      if (userId) {
        await fetchMessages(true);
        if (isSuperAdmin) await loadUsers();
      } else { setLoading(false); }
    };
    init();
  }, [userId, isSuperAdmin]);

  useEffect(() => {
    if (page > 1 && userId) fetchMessages();
  }, [page, userId]);

  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(() => fetchMessages(true), 30000);
    return () => clearInterval(interval);
  }, [userId, fetchMessages]);

  // ==============================================
  // MARK AS READ
  // ==============================================
  const markAsRead = async (messageId) => {
    if (!userId || !messageId) return;
    try {
      await axios.post(`${API_BASE_URL}/root_communication.php`, {
        action: 'mark_read', message_id: messageId, user_id: userId
      });
      setAllMessages(prev => prev.map(msg => msg && msg.id === messageId ? { ...msg, is_read_by_user: 1 } : msg));
      setFilteredMessages(prev => prev.map(msg => msg && msg.id === messageId ? { ...msg, is_read_by_user: 1 } : msg));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) { console.error('Error marking message as read:', error); }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    try {
      await axios.post(`${API_BASE_URL}/root_communication.php`, {
        action: 'mark_all_read', user_id: userId
      });
      setAllMessages(prev => prev.map(msg => msg ? { ...msg, is_read_by_user: 1 } : msg));
      setFilteredMessages(prev => prev.map(msg => msg ? { ...msg, is_read_by_user: 1 } : msg));
      setUnreadCount(0);
    } catch (error) { console.error('Error marking all as read:', error); }
  };

  // ==============================================
  // SEND QUICK MESSAGE
  // ==============================================
  const sendQuickMessage = async () => {
    if (!userId || !isSuperAdmin || (!quickMessage.trim() && !selectedFile)) return;
    try {
      setSending(true);
      setError(null);
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(quickMessage)) !== null) mentions.push(match[1]);
      
      let recipientType = 'all_users';
      let recipientIds = [];
      let recipientRoles = [];
      
      if (mentions.length > 0) {
        if (mentions.includes('all')) {
          recipientType = 'all_users';
        } else {
          const roleMentions = mentions.filter(m => allRoles && allRoles.find(r => r && r.value === m));
          const userMentions = mentions.filter(m => allUsers && allUsers.find(u => u && (u.username === m || u.fullname === m)));
          
          if (roleMentions.length > 0 && userMentions.length === 0) {
            recipientType = 'role_based';
            recipientRoles = roleMentions;
          } else if (userMentions.length > 0 && roleMentions.length === 0) {
            recipientType = userMentions.length === 1 ? 'specific_user' : 'multiple_users';
            recipientIds = userMentions.map(username => {
              const user = allUsers.find(u => u && (u.username === username || u.fullname === username));
              return user ? user.id : null;
            }).filter(Boolean);
          } else {
            recipientType = 'all_users';
          }
        }
      }
      
      const cleanMessage = quickMessage.replace(/@\w+\s*/g, '').trim();
      
      const formData = new FormData();
      formData.append('action', 'send_message');
      formData.append('sender_id', userId);
      formData.append('sender_role', 'super_admin');
      formData.append('recipient_type', recipientType);
      formData.append('recipient_ids', JSON.stringify(recipientIds));
      formData.append('recipient_roles', JSON.stringify(recipientRoles));
      formData.append('message_type', 'information');
      formData.append('message_title', 'System Update');
      formData.append('message_content', cleanMessage || quickMessage.trim());
      formData.append('priority', 'medium');
      if (selectedFile) formData.append('attachment', selectedFile);
      
      const response = await axios.post(`${API_BASE_URL}/root_communication.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data && response.data.success) {
        setQuickMessage('');
        setSelectedFile(null);
        setFilePreview(null);
        setShowQuickEmojiPicker(false);
        setInsertedIcons([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        await fetchMessages(true);
      } else {
        setError((response.data && response.data.error) || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError((error.response && error.response.data && error.response.data.error) || error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!e) return;
    if (showMentionDropdown) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuickMessage();
    }
  };

  // ==============================================
  // RENDER FUNCTIONS
  // ==============================================
  const getMessageTypeInfo = (type) => {
    return messageTypes[type] || { label: type || 'Unknown', icon: <Info size={12} />, color: '#6B7280' };
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const formatMessageDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    if (!messages || !Array.isArray(messages)) return groups;
    messages.forEach(msg => {
      if (!msg) return;
      const dateKey = formatMessageDate(msg.created_at);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(msg);
    });
    return groups;
  };

  const getUserDisplayName = (recipientName) => {
    if (!recipientName) return 'User';
    try {
      const parsed = JSON.parse(recipientName);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const firstName = parsed.find(name => name && typeof name === 'string');
        return firstName || 'User';
      }
    } catch (e) {}
    return typeof recipientName === 'string' ? recipientName : 'User';
  };

  // Render icon names in message content
  const renderMessageWithIcons = (content) => {
    if (!content) return '';
    // Replace :iconName: with SVG icon placeholder
    return content.replace(/:(\w+):/g, (match, iconName) => {
      const item = Object.values(EMOJI_CATEGORIES_DATA)
        .flatMap(cat => cat.items)
        .find(i => i.name === iconName);
      if (item) {
        return `<span class="inline-icon" title="${item.name}">${item.name}</span>`;
      }
      return match;
    });
  };

  const renderMessageContent = (message) => {
    if (!message) return null;
    const greeting = `DEAR ${getUserDisplayName(message.recipient_name || 'User')},`;
    
    return (
      <div className="formatted-message">
        <div className="message-greeting">{greeting}</div>
        <div className="message-body">
          <div 
            className="message-text-content"
            dangerouslySetInnerHTML={{ 
              __html: parseMessageWithMentions(renderMessageWithIcons(message.message_content || '')) 
            }}
          />
        </div>
        {message.attachment_name && (
          <div className="message-attachment">
            <a 
              href={`${API_BASE_URL}/${message.attachment_path}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="attachment-link"
            >
              {message.attachment_type && message.attachment_type.startsWith('image/') ? <FileImage size={20} /> : <File size={20} />}
              <span className="attachment-name">{message.attachment_name}</span>
              {message.attachment_size ? <span className="attachment-size">{formatFileSize(message.attachment_size)}</span> : null}
              <Download size={16} />
            </a>
          </div>
        )}
        <div className="message-signature">
          <div>Best Regards,</div>
          <div className="signature-company">OURZEST PVT LTD.</div>
          <div className="signature-team">SUPPORT TEAM</div>
        </div>
      </div>
    );
  };

  const renderChatMessage = (message) => {
    if (!message) return null;
    const isRead = message.is_read_by_user === 1;
    const messageTypeInfo = getMessageTypeInfo(message.message_type);
    const isOwn = message.sender_id === userId;

    return (
      <div 
        className={`chat-message ${isOwn ? 'own' : 'received'}`}
        onClick={() => !isRead && markAsRead(message.id)}
      >
        <div className="message-bubble-container">
          <div className="chat-bubble" style={!isOwn ? { borderLeft: `4px solid ${messageTypeInfo.color}` } : {}}>
            {!isOwn && (
              <div className="chat-sender-info">
                <img src={ourzestLogo} alt="OURZEST" className="chat-sender-avatar" />
                <div>
                  <span className="chat-sender-name">OURZEST PVT LTD</span>
                 
                </div>
              </div>
            )}
            <div className="chat-message-content">
              {renderMessageContent(message)}
            </div>
            <div className="chat-message-footer">
              <span className="chat-message-time">{formatTime(message.created_at)}</span>
              {isOwn && (
                <span className="chat-message-status">
                  {isRead ? <CheckCheck size={14} className="read-icon" /> : <Check size={14} className="delivered-icon" />}
                </span>
              )}
             
            </div>
          </div>
          {!isRead && !isOwn && <div className="unread-dot" />}
        </div>
      </div>
    );
  };

  // ==============================================
  // MAIN RENDER
  // ==============================================
  if (!userId && !loading) {
    return (
      <div className="message-system">
        <div className="empty-state">
          <Bell size={48} className="empty-icon" />
          <h3>Please Log In</h3>
          <p>You need to be logged in to view messages</p>
        </div>
      </div>
    );
  }

  const displayMessages = (filteredMessages.length > 0 || searchQuery) ? filteredMessages : allMessages;
  const groupedMessages = groupMessagesByDate(displayMessages);

  return (
    <div className={`message-system desktop ${darkMode ? 'ozui-dark' : ''}`}>
      <div className="whatsapp-container">
        <div className="chat-panel full-width">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-contact-info">
              <div className="chat-header-sender">
                <img src={ourzestLogo} alt="OURZEST" className="header-logo" />
                <div>
                  <span className="chat-contact-name">OURZEST PVT LTD</span>
                 
                </div>
              </div>
            </div>
            <div className="chat-header-actions">
              <div className="search-wrapper">
                <Search size={18} className="search-icon-header" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value || '')}
                  className="search-input-header"
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                    <X size={16} />
                  </button>
                )}
              </div>
              {unreadCount > 0 && (
                <button className="mark-all-btn" onClick={markAllAsRead}>
                  <CheckCheck size={16} /> Mark All Read ({unreadCount})
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="chat-messages-container">
            <div className="chat-messages">
              {loading ? (
                <div className="loading-spinner">
                  <Loader2 size={24} className="spinner-icon" />
                 
                </div>
              ) : displayMessages.length === 0 ? (
                <div className="empty-state">
                  <Bell size={48} className="empty-icon" />
                  <h3>{searchQuery ? 'No matching messages' : 'No messages yet'}</h3>
                  <p>{searchQuery ? 'Try a different search term' : 'Check back later for updates from OURZEST PVT LTD'}</p>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([date, msgs]) => (
                  <React.Fragment key={date}>
                    <div className="date-divider"><span>{date}</span></div>
                    {msgs.map((msg, index) => (
                      <React.Fragment key={msg ? msg.id : index}>
                        {renderChatMessage(msg)}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))
              )}
              {hasMore && !loading && displayMessages.length > 0 && (
                <button className="load-more-btn" onClick={() => setPage(prev => prev + 1)}>
                  Load More Messages
                </button>
              )}
            </div>
          </div>

          {/* Bottom Input */}
        {/* Bottom Input - Only visible to Super Admin */}
{isSuperAdmin && (
  <div className="chat-input-container">
    <div className="emoji-button-wrapper" ref={emojiPickerRef}>
      <button className="chat-emoji-btn" onClick={() => setShowQuickEmojiPicker(!showQuickEmojiPicker)}>
        <Smile size={22} />
      </button>
      {showQuickEmojiPicker && (
        <div className="emoji-picker-container">
          <BuiltInEmojiPicker onEmojiSelect={handleEmojiSelect} darkMode={darkMode} />
        </div>
      )}
    </div>
    
    <button className="chat-attach-btn" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
      <Paperclip size={22} />
    </button>
    <input 
      ref={fileInputRef} 
      type="file" 
      onChange={handleFileSelect} 
      style={{ display: 'none' }}
      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar" 
    />
    
    <div className="message-input-wrapper">
      {filePreview && (
        <div className="file-preview-bar">
          {filePreview.type === 'image' ? (
            <img src={filePreview.url} alt="Preview" className="file-preview-img" />
          ) : (
            <div className="file-preview-icon"><File size={20} /></div>
          )}
          <span className="file-preview-name">{filePreview.name}</span>
          <button className="remove-file-btn" onClick={removeFile}><X size={14} /></button>
        </div>
      )}
      <div className="input-with-mentions">
        <input
          ref={quickInputRef}
          type="text"
          placeholder="Type message... Use @all, @admin_fabrics, @username"
          value={quickMessage}
          onChange={handleQuickMessageChange}
          onKeyDown={handleKeyDown}
          className="chat-input"
        />
        {showMentionDropdown && mentionResults.length > 0 && (
          <div className="mention-dropdown" ref={mentionDropdownRef}>
            {mentionResults.map((item, index) => (
              <div key={index} className="mention-item" onClick={() => selectMention(item)}>
                <span className="mention-icon-wrapper">{item.icon}</span>
                <div className="mention-info">
                  <span className="mention-label">{item.label}</span>
                  <span className="mention-desc">{item.description}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    
    <button 
      className="chat-send-btn"
      onClick={sendQuickMessage}
      disabled={sending || (!quickMessage.trim() && !selectedFile)}
    >
      {sending ? <Loader2 size={20} className="spinner-icon" /> : <Send size={20} />}
    </button>
  </div>
)}
        </div>
      </div>
      
      {error && (
        <div className="error-toast">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
    </div>
  );
};

// Need to import RefreshCw for system_update
import { RefreshCw, AlertCircle } from 'lucide-react';

export default MessageSystem;